import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaChartLine,
  FaHandshake,
  FaShieldAlt,
  FaSearch,
  FaProjectDiagram,
  FaRocket,
  FaHeadset,
  FaBuilding,
  FaUserTie,
  FaStore,
  FaLightbulb,
  FaExclamationTriangle,
  FaCheckCircle,
  FaIndustry,
  FaHome,
  FaCogs,
  FaGlobe
} from 'react-icons/fa';
import './ConsultoriaMarruecosES.css';

const PILLAR_CONFIG = [
  { key: 'a', icon: FaChartLine, itemIcons: [FaSearch, FaBuilding, FaShieldAlt] },
  { key: 'b', icon: FaHandshake, itemIcons: [FaUserTie, FaProjectDiagram, FaGlobe] },
  { key: 'c', icon: FaShieldAlt, itemIcons: [FaCheckCircle, FaChartLine, FaHandshake] }
];
const METHODOLOGY_ICONS = [FaSearch, FaProjectDiagram, FaRocket, FaHeadset];
const CLIENT_ICONS = [FaUserTie, FaChartLine, FaStore, FaLightbulb];
const USE_CASE_ICONS = [FaIndustry, FaHome, FaCogs];

const ConsultoriaMarruecosES = () => {
  const { t } = useTranslation();
  const growthList = t('consultoriaPage.growth.list', { returnObjects: true });
  const methodologySteps = t('consultoriaPage.methodology.steps', { returnObjects: true });
  const clients = t('consultoriaPage.clients.items', { returnObjects: true });
  const painItems = t('consultoriaPage.painPoints.items', { returnObjects: true });
  const useCases = t('consultoriaPage.useCases.items', { returnObjects: true });
  const bilateralList = t('consultoriaPage.bilateral.list', { returnObjects: true });

  return (
    <div className="consultoria-es-page">
      <section className="consultoria-es-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-3 text-center mb-4 mb-lg-0">
              <img src="/images/logo.jpeg" alt={t('common.altLogo')} className="consultoria-es-hero-logo" />
            </div>
            <div className="col-lg-9 text-center text-lg-start">
              <p className="consultoria-es-eyebrow">{t('consultoriaPage.hero.eyebrow')}</p>
              <h1 className="consultoria-es-hero-title">{t('consultoriaPage.hero.title')}</h1>
              <p className="consultoria-es-hero-lead">{t('consultoriaPage.hero.lead')}</p>
              <div className="consultoria-es-hero-cta">
                <Link to="/diagnostic" className="btn consultoria-es-btn-primary">
                  {t('consultoriaPage.hero.cta')}
                </Link>
              </div>
              <p className="consultoria-es-hero-note">{t('consultoriaPage.hero.note')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="consultoria-es-section">
        <div className="container">
          <h2 className="consultoria-es-h2">{t('consultoriaPage.growth.title')}</h2>
          <p className="consultoria-es-intro">{t('consultoriaPage.growth.intro')}</p>
          <ul className="consultoria-es-list consultoria-es-list-check">
            {growthList.map((item, index) => (
              <li key={index}>
                <strong>{item.strong}</strong>
                {item.text}
              </li>
            ))}
          </ul>
          <p className="consultoria-es-highlight">
            {t('consultoriaPage.growth.highlight')}
            <strong>{t('consultoriaPage.growth.highlightStrong')}</strong>
            {t('consultoriaPage.growth.highlightEnd')}
          </p>
        </div>
      </section>

      <section className="consultoria-es-section consultoria-es-section-alt">
        <div className="container">
          <h2 className="consultoria-es-h2">{t('consultoriaPage.pillars.title')}</h2>
          <p className="consultoria-es-intro text-center mb-5">{t('consultoriaPage.pillars.intro')}</p>
          <div className="row g-4">
            {PILLAR_CONFIG.map(({ key, icon: PillarIcon, itemIcons }) => {
              const items = t(`consultoriaPage.pillars.${key}.items`, { returnObjects: true });
              return (
                <div key={key} className="col-lg-4">
                  <div className="consultoria-es-pillar">
                    <div className="consultoria-es-pillar-icon"><PillarIcon /></div>
                    <h3 className="consultoria-es-h3">{t(`consultoriaPage.pillars.${key}.title`)}</h3>
                    <p className="consultoria-es-pillar-sub">{t(`consultoriaPage.pillars.${key}.subtitle`)}</p>
                    <ul className="consultoria-es-list">
                      {items.map((item, index) => {
                        const ItemIcon = itemIcons[index];
                        return (
                          <li key={index}>
                            <ItemIcon className="consultoria-es-li-icon" aria-hidden />
                            <span><strong>{item.strong}</strong>{item.text}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="consultoria-es-section">
        <div className="container">
          <h2 className="consultoria-es-h2 text-center">{t('consultoriaPage.methodology.title')}</h2>
          <p className="consultoria-es-intro text-center mb-5">{t('consultoriaPage.methodology.intro')}</p>
          <div className="row g-4">
            {methodologySteps.map((item, index) => (
              <div key={item.step} className="col-md-6 col-lg-3">
                <div className="consultoria-es-step-card">
                  <span className="consultoria-es-step-num">{item.step}</span>
                  <div className="consultoria-es-step-icon">{METHODOLOGY_ICONS[index]}</div>
                  <h3 className="consultoria-es-step-title">{item.title}</h3>
                  <p className="consultoria-es-step-text">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="consultoria-es-section consultoria-es-section-alt">
        <div className="container">
          <h2 className="consultoria-es-h2 text-center">{t('consultoriaPage.clients.title')}</h2>
          <div className="row g-4 justify-content-center mt-2">
            {clients.map((c, index) => (
              <div key={index} className="col-sm-6 col-lg-3">
                <div className="consultoria-es-client-card">
                  <div className="consultoria-es-client-icon">{CLIENT_ICONS[index]}</div>
                  <h3 className="consultoria-es-h3 consultoria-es-h3-sm">{c.title}</h3>
                  <p className="consultoria-es-client-text">{c.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="consultoria-es-section">
        <div className="container">
          <h2 className="consultoria-es-h2">{t('consultoriaPage.painPoints.title')}</h2>
          <p className="consultoria-es-intro">{t('consultoriaPage.painPoints.intro')}</p>
          <div className="row g-4">
            <div className="col-lg-6">
              <ul className="consultoria-es-pain-list">
                {painItems.map((item, index) => (
                  <li key={index}>
                    <FaExclamationTriangle className="consultoria-es-pain-icon" aria-hidden />
                    <div>
                      <strong>{item.strong}</strong>
                      <span>{item.text}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-lg-6">
              <div className="consultoria-es-solution-box">
                <FaCheckCircle className="consultoria-es-solution-icon" aria-hidden />
                <h3 className="consultoria-es-h3">{t('consultoriaPage.painPoints.solution.title')}</h3>
                <p>
                  {t('consultoriaPage.painPoints.solution.text')}
                  <strong>{t('consultoriaPage.painPoints.solution.strong1')}</strong>
                  {t('consultoriaPage.painPoints.solution.and')}
                  <strong>{t('consultoriaPage.painPoints.solution.strong2')}</strong>
                  {t('consultoriaPage.painPoints.solution.end')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="consultoria-es-section consultoria-es-section-alt">
        <div className="container">
          <h2 className="consultoria-es-h2 text-center">{t('consultoriaPage.useCases.title')}</h2>
          <div className="row g-4 mt-2">
            {useCases.map((u, index) => (
              <div key={index} className="col-md-4">
                <div className="consultoria-es-use-card">
                  <div className="consultoria-es-use-icon">{USE_CASE_ICONS[index]}</div>
                  <h3 className="consultoria-es-h3 consultoria-es-h3-sm">{u.title}</h3>
                  <p>{u.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="consultoria-es-section consultoria-es-bilateral">
        <div className="container">
          <h2 className="consultoria-es-h2">{t('consultoriaPage.bilateral.title')}</h2>
          <p className="consultoria-es-intro">{t('consultoriaPage.bilateral.intro')}</p>
          <ul className="consultoria-es-list consultoria-es-list-inline">
            {bilateralList.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="consultoria-es-final-cta">
        <div className="container text-center">
          <h2 className="consultoria-es-h2 consultoria-es-h2-light">{t('consultoriaPage.finalCta.title')}</h2>
          <p className="consultoria-es-final-lead">{t('consultoriaPage.finalCta.lead')}</p>
          <Link to="/diagnostic" className="btn consultoria-es-btn-primary consultoria-es-btn-lg">
            {t('consultoriaPage.finalCta.button')}
          </Link>
          <p className="consultoria-es-trust-line">{t('consultoriaPage.finalCta.trust')}</p>
        </div>
      </section>
    </div>
  );
};

export default ConsultoriaMarruecosES;
